/** Dependency-free 'YYYY-MM-DD' arithmetic, matching the engine's own date helpers. */

const DAY_MS = 86_400_000;

const toDayNumber = (date: string): number => {
  const [y, m, d] = date.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
};

const fromDayNumber = (days: number): string => {
  const d = new Date(days * DAY_MS);
  return [
    String(d.getUTCFullYear()),
    String(d.getUTCMonth() + 1).padStart(2, '0'),
    String(d.getUTCDate()).padStart(2, '0'),
  ].join('-');
};

export const isIsoDate = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

export const addDays = (date: string, days: number): string => fromDayNumber(toDayNumber(date) + days);

/** ISO day of week, 1 = Monday … 7 = Sunday. */
export const dowOf = (date: string): number => (((toDayNumber(date) + 3) % 7) + 7) % 7 + 1;

export const mondayOf = (date: string): string => addDays(date, 1 - dowOf(date));

export const todayIso = (): string => {
  const now = new Date();
  return [
    String(now.getFullYear()),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
};

export const daysBetween = (from: string, to: string): number => toDayNumber(to) - toDayNumber(from);

/** « 43 semaines »: week 1 is the start week and the race week closes the count, as on the progress screens. */
export const weeksUntil = (start: string, race: string): number =>
  daysBetween(mondayOf(start), mondayOf(race)) / 7 + 1;

export const daysOfWeek = (monday: string): string[] =>
  Array.from({ length: 7 }, (_, i) => addDays(monday, i));

const MONTHS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

/** « 6 – 12 octobre » — one month name when the week does not straddle two. */
export const formatWeekRange = (monday: string): string => {
  const sunday = addDays(monday, 6);
  const [, m1, d1] = monday.split('-').map(Number);
  const [, m2, d2] = sunday.split('-').map(Number);
  return m1 === m2
    ? `${d1} – ${d2} ${MONTHS[m2 - 1]}`
    : `${d1} ${MONTHS[m1 - 1]} – ${d2} ${MONTHS[m2 - 1]}`;
};

/** « 12 octobre 2026 » — a full date, for the summary. */
export const formatLongDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

const SHORT_MONTHS = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];

/** « 12 oct. » or « 12 oct. 2026 » — compact, for tiles and ranges. */
export const formatShortDate = (iso: string, withYear = false): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${SHORT_MONTHS[m - 1]}${withYear ? ` ${y}` : ''}`;
};

/** « 22 mars → 28 mars 2027 » — the year only once, at the end. */
export const formatShortRange = (from: string, to: string): string =>
  `${formatShortDate(from, from.slice(0, 4) !== to.slice(0, 4))} → ${formatShortDate(to, true)}`;

/**
 * DayPicker speaks `Date`, this module speaks 'YYYY-MM-DD'. Both helpers build and read the date in
 * **local** time at noon: midnight would let a negative UTC offset — or DayPicker's own noon
 * normalisation — land on the previous day.
 */
export const isoToDate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d, 12);
};

export const dateToIso = (date: Date): string =>
  [
    String(date.getFullYear()),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
